import { CartLinesUpdateEvent } from '@shopify/events';

/* Embroidery picture in the cart (Jerry, 1 Nov 2026): tapping it opens a bigger picture in a pop-up on the same page,
   instead of opening the image file. One listener on the document, so it keeps working after the cart re-renders. */
if (!window.__mtEmbZoom) {
  window.__mtEmbZoom = true;
  let dlg, views = [], alt = '';
  const show = (i) => {
    if (!views[i]) return;
    const img = dlg.querySelector('img');
    img.src = views[i][0];
    img.alt = alt + ', ' + views[i][1].toLowerCase();
    dlg.querySelectorAll('.mt-emb-zoom__views button').forEach((x, k) => x.setAttribute('aria-pressed', String(k === i)));
  };
  const make = () => {
    dlg = document.createElement('dialog');
    dlg.className = 'mt-emb-zoom';
    dlg.setAttribute('aria-label', 'Your embroidery design');
    dlg.innerHTML =
      '<div class="mt-emb-zoom__panel"><p class="mt-emb-zoom__title">Your embroidery design</p>' +
      '<div class="mt-emb-zoom__views" role="group" aria-label="Choose a view"><button type="button" data-v="0"></button><button type="button" data-v="1"></button></div>' +
      '<img class="mt-emb-zoom__img" alt="">' +
      '<button type="button" class="button mt-emb-zoom__close">Close</button></div>';
    dlg.addEventListener('click', (e) => {
      if (e.target === dlg || e.target.closest('.mt-emb-zoom__close')) return dlg.close();
      const v = e.target.closest('[data-v]');
      if (v) show(Number(v.dataset.v));
    });
    document.body.appendChild(dlg);
  };
  document.addEventListener('click', (e) => {
    const b = e.target.closest && e.target.closest('[data-mt-emb-zoom]');
    if (!b) return;
    e.preventDefault();
    if (!dlg || !dlg.isConnected) make();
    const first = b.dataset.mtEmbView === 'Whole bed' ? 'Whole bed' : 'Close-up';
    views = [[b.dataset.mtEmbZoom, first]];
    if (b.dataset.mtEmbZoom2) views.push([b.dataset.mtEmbZoom2, first === 'Close-up' ? 'Whole bed' : 'Close-up']);
    /* Buttons always read Close-up, then Whole bed. */
    views.sort((x, y) => (x[1] === 'Close-up' ? -1 : 1) - (y[1] === 'Close-up' ? -1 : 1));
    alt = b.dataset.mtEmbAlt || 'Your embroidery design';
    const box = dlg.querySelector('.mt-emb-zoom__views');
    box.hidden = views.length < 2;
    box.querySelectorAll('button').forEach((x, i) => (x.textContent = views[i] ? views[i][1] : ''));
    show(views.findIndex((x) => x[1] === first));
    dlg.showModal();
  });

  /* Take embroidery lines out of the cart and let the cart, drawer and count redraw. */
  const removeKeys = async (keys) => {
    const updates = {};
    keys.forEach((k) => (updates[k] = 0));
    const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean))].join(',');
    let resolve, reject;
    const promise = new Promise((a, r) => { resolve = a; reject = r; });
    promise.catch(() => {});
    document.dispatchEvent(new CartLinesUpdateEvent({ action: 'remove', context: 'cart', lines: keys.map((id) => ({ id, quantity: 0 })), promise }));
    try {
      const r = await fetch(String(Theme.routes.cart_update_url || '/cart/update').replace(/(\.js)?$/, '.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections, sections_url: location.pathname }),
      });
      const d = await r.json();
      if (!r.ok || d.status) throw new Error(d.description || 'Remove failed');
      resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(d), detail: { items: d.items, sections: d.sections, source: 'mitodo-embroidery', didError: false } });
    } catch (err) {
      reject(err);
      throw err;
    }
  };

  /* One "Remove embroidery" button takes out every embroidery line of that design (Jerry, 1 Nov 2026: one row per design). */
  document.addEventListener('click', async (e) => {
    const b = e.target.closest && e.target.closest('[data-mt-emb-remove]');
    if (!b) return;
    e.preventDefault();
    if (b.disabled) return;
    b.disabled = true;
    try {
      await removeKeys(b.dataset.mtEmbRemove.split(',').filter(Boolean));
    } catch (err) {
      b.disabled = false;
      console.warn('[mitodo-embroidery]', err);
    }
  });

  /* Embroidery for a set that has left the cart goes with it (Jerry, 1 Nov 2026). The bed's own embroidery is attached to the
     bed and leaves with it already; a set can't hold attached lines, so its embroidery is tied to it by "_emb" only. The cart
     marks such leftovers with data-mt-emb-orphan. Checked against the live cart first, and never while embroidery is being added. */
  const tried = new Set();
  let busy = false;
  const sweep = async () => {
    if (busy || window.__mtEmbAdding) return;
    const rows = [...document.querySelectorAll('[data-mt-emb-orphan]')];
    if (!rows.length) return;
    busy = true;
    try {
      const cart = await fetch(String(Theme.routes.cart_url || '/cart').replace(/(\.js)?$/, '.js'), { cache: 'no-store', headers: { Accept: 'application/json' } }).then((r) => r.json());
      if (window.__mtEmbAdding) return;
      const owned = new Set(cart.items.filter((i) => i.handle !== 'mitodo-embroidery' && i.properties && i.properties._emb).map((i) => i.properties._emb));
      const keys = cart.items
        .filter((i) => i.handle === 'mitodo-embroidery' && i.properties && i.properties._emb && !owned.has(i.properties._emb) && !tried.has(i.key))
        .map((i) => i.key);
      if (!keys.length) return;
      keys.forEach((k) => tried.add(k));
      await removeKeys(keys);
    } catch (err) {
      console.warn('[mitodo-embroidery] leftover', err);
    } finally {
      busy = false;
    }
  };
  let queued = 0;
  const later = () => { if (!queued) queued = setTimeout(() => { queued = 0; sweep(); }, 400); };
  new MutationObserver(later).observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-mt-emb-orphan'] });
  later();
}
